import { useState, useEffect, useMemo } from 'react';
import { InvoiceFileProcessingStatusDto } from 'src/generated/services/TFinancialApi';
import { useUploadProgress } from 'src/hooks/useUploadProgress';
import { processingStatuses } from 'src/presentations/components/molecules/uploadInvoice/configs';

/**
 * Upload Data Hook - Manages invoice processing data
 *
 * NEW ARCHITECTURE:
 * - SSE acts as trigger to refetch API
 * - Uses API data directly (single source of truth)
 * - No more data merging complexity
 * - Processing status computation
 * - Processing statistics
 */
export const useUploadData = (batchId: string | null, enabled: boolean = true) => {
  // Get data from API (SSE triggers refetch internally)
  const { data: apiData, isLoading, isConnected, refetch } = useUploadProgress(batchId, enabled);

  // Processing state
  const [isAllProcessCompleted, setIsAllProcessCompleted] = useState<boolean>(false);

  /**
   * Check if all processing is completed
   */
  useEffect(() => {
    if (apiData && apiData.length > 0) {
      const completedItems = apiData.filter((serviceProvider) => {
        // Check all invoice groups within the service provider
        const allInvoiceGroupsCompleted = serviceProvider?.invoiceGroups?.every((invoiceGroup) => {
          // Check all files within each invoice group
          return invoiceGroup?.invoiceFileProcessingStatusList?.every(
            (statusItem: InvoiceFileProcessingStatusDto) => !processingStatuses.includes(statusItem?.status)
          );
        });
        return allInvoiceGroupsCompleted;
      });

      const allCompleted = apiData.length === completedItems.length;
      setIsAllProcessCompleted(allCompleted);
    }
  }, [apiData]);

  /**
   * Reset all data for new upload session
   */
  const resetData = () => {
    setIsAllProcessCompleted(false);
    refetch();
  };

  /**
   * Get processing statistics
   */
  const stats = useMemo(() => {
    if (!apiData || apiData.length === 0) {
      return {
        total: 0,
        completed: 0,
        processing: 0,
        failed: 0,
        percentage: 0,
      };
    }

    let total = 0;
    let completed = 0;
    let processing = 0;
    let failed = 0;

    // Iterate through service providers
    apiData.forEach((serviceProvider) => {
      // Iterate through invoice groups within each service provider
      serviceProvider.invoiceGroups?.forEach((invoiceGroup) => {
        // Iterate through files within each invoice group
        invoiceGroup.invoiceFileProcessingStatusList?.forEach((item) => {
          total += 1;

          if (processingStatuses.includes(item.status)) {
            processing += 1;
          } else if (item.status === 'COMPLETE') {
            completed += 1;
          } else {
            failed += 1;
          }
        });
      });
    });

    return {
      total,
      completed,
      processing,
      failed,
      percentage: total > 0 ? Math.round((completed / total) * 100) : 0,
    };
  }, [apiData]);

  return {
    // Processed Data - directly from API
    processingData: apiData || [],

    // Processing Status
    isProcessingComplete: isAllProcessCompleted,
    processingStats: stats,

    // Connection & Loading
    isLoading,
    isSSEConnected: isConnected,

    // Data State
    hasData: !!apiData && apiData.length > 0,

    // Actions
    resetProcessingData: resetData,
    refetch,

    // Debug info
    debugInfo: {
      batchId,
      enabled,
      dataSource: 'API (SSE triggers refetch)',
    },
  };
};
